// Leads: fetch one diploma's list and export it as CSV. The file starts with a
// UTF-8 BOM so Excel opens Arabic names correctly (SPEC §D.4 Leads).
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import type { DiplomaLead, LeadKind } from 'src/@types/landing/landing.contract';
import { formatDateTime } from './format';

export async function fetchLeads(diplomaId: string, kind?: LeadKind | ''): Promise<DiplomaLead[]> {
  // limit=500 is the API maximum (§C.9); the default page is 200.
  const base = APIS.DIPLOMAS.LEADS(diplomaId, kind || undefined);
  const res = await axiosInstance.get(`${base}${base.indexOf('?') >= 0 ? '&' : '?'}limit=500`);
  const body = res.data?.data ?? res.data;
  const list = Array.isArray(body?.leads) ? body.leads : Array.isArray(body) ? body : [];
  return list as DiplomaLead[];
}

// A phone the API stored in E.164 ('+' then 8–15 digits, no leading 0).
const E164 = /^\+[1-9]\d{7,14}$/;

/**
 * One quoted CSV cell. Any value a spreadsheet could run as a formula (it
 * starts with = + - @ or a TAB / CR) gets a leading apostrophe, in every
 * column: a public lead can put '+1+HYPERLINK(…)' in its name or a UTM. The
 * only exemption is the Phone column when the whole value is a plain E.164
 * number.
 */
export const cell = (v: unknown, isPhone = false): string => {
  const s = v == null ? '' : String(v);
  const risky = /^[=+\-@\t\r]/.test(s) && !(isPhone && E164.test(s));
  const safe = risky ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
};

const waLink = (phone: string) => {
  const digits = (phone || '').replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '';
};

export const LEAD_COLUMNS: { label: string; get: (l: DiplomaLead) => unknown; phone?: boolean }[] = [
  { label: 'Date (Cairo)', get: (l) => formatDateTime(l.createdAt) },
  { label: 'Kind', get: (l) => l.kind },
  { label: 'Name', get: (l) => l.name },
  { label: 'Phone', get: (l) => l.phone, phone: true },
  // Text a spreadsheet never turns into 2.01012E+11, and one click to the chat.
  { label: 'WhatsApp link', get: (l) => waLink(l.phone) },
  { label: 'Email', get: (l) => l.email },
  { label: 'Page language', get: (l) => l.locale },
  { label: 'Batch id', get: (l) => l.batchId || '' },
  { label: 'UTM source', get: (l) => l.utm?.source },
  { label: 'UTM medium', get: (l) => l.utm?.medium },
  { label: 'UTM campaign', get: (l) => l.utm?.campaign },
  { label: 'Marketing consent', get: (l) => (l.marketingConsent ? 'yes' : 'no') },
  { label: 'Page URL', get: (l) => l.pageUrl },
];

export function leadsToCsv(leads: DiplomaLead[]): string {
  const rows = [LEAD_COLUMNS.map((c) => cell(c.label)).join(',')];
  leads.forEach((l) => rows.push(LEAD_COLUMNS.map((c) => cell(c.get(l), !!c.phone)).join(',')));
  return `﻿${rows.join('\r\n')}\r\n`;
}

export function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Fetch + download: `<slug>-<kind|all>-leads-<YYYY-MM-DD>.csv`. Returns the row count. */
export async function exportLeadsCsv(diplomaId: string, slug: string, kind?: LeadKind | ''): Promise<number> {
  const leads = await fetchLeads(diplomaId, kind);
  const day = new Date().toISOString().slice(0, 10);
  downloadCsv(`${slug || 'diploma'}-${kind || 'all'}-leads-${day}.csv`, leadsToCsv(leads));
  return leads.length;
}
